import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { SITE } from "../data/site";
// bundled (content-hashed) logo URLs → served from /static with 1-year immutable
// caching (the /assets copies stay for JSON-LD/SEO which need stable URLs)
import logo400 from "../assets/dcc-logo-400.webp";
import logo800 from "../assets/dcc-logo-800.webp";

export function Wordmark({ className = "h-8 md:h-9" }) {
  return (
    <Link to="/" data-testid="logo-home-link" className="flex items-center" aria-label="Duct Clean Co. — home">
      <img
        src={logo400}
        srcSet={`${logo400} 400w, ${logo800} 800w`}
        sizes="(max-width: 768px) 160px, 194px"
        alt="Duct Clean Co."
        width="400"
        height="66"
        className={`w-auto ${className}`}
        fetchPriority="high"
      />
    </Link>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
          scrolled ? "border-zinc-200 bg-white/85 backdrop-blur-xl" : "border-transparent bg-transparent"
        }`}
      >
        <div className="container-x flex h-16 items-center justify-between md:h-20">
          <Wordmark />
          <nav aria-label="Primary" className="hidden items-center gap-6 xl:flex">
            {SITE.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `relative pb-1.5 text-[13px] font-medium tracking-wide transition-colors hover:text-charcoal ${
                    isActive ? "text-charcoal" : "text-zinc-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      aria-hidden="true"
                      className={`absolute -bottom-0.5 left-0 h-[3px] bg-dcc transition-all duration-300 ${isActive ? "w-full" : "w-0"}`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to={SITE.cta.to} data-testid="nav-survey-cta" className="btn-primary hidden !px-5 !py-3 text-xs xl:inline-flex">
              {SITE.cta.label}
              <ArrowUpRight size={15} aria-hidden="true" />
            </Link>
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="grid h-11 w-11 place-items-center border border-zinc-300 text-charcoal transition-colors hover:border-dcc hover:text-dcc xl:hidden"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="noise-dark fixed inset-0 z-40 flex flex-col pt-24 xl:hidden"
          >
            <nav aria-label="Mobile" className="container-x flex flex-1 flex-col gap-1 overflow-y-auto pb-10">
              {SITE.nav.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={reduce ? { opacity: 0 } : { opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    className={({ isActive }) =>
                      `flex items-baseline gap-4 border-b border-zinc-800 py-4 font-display text-3xl font-semibold uppercase tracking-tight transition-colors ${
                        isActive ? "text-dcc" : "text-zinc-100"
                      }`
                    }
                  >
                    <span className="font-mono text-xs text-zinc-400">{String(i + 1).padStart(2, "0")}</span>
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link to={SITE.cta.to} data-testid="mobile-nav-survey-cta" className="btn-primary mt-8 w-full">
                {SITE.cta.label}
                <ArrowUpRight size={16} aria-hidden="true" />
              </Link>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <a href={`tel:${SITE.contact.phone.replace(/\s/g, "")}`} data-testid="mobile-nav-call-btn"
                  className="flex min-h-[48px] items-center justify-center border border-zinc-700 font-mono text-xs uppercase tracking-[0.18em] text-zinc-200 transition-colors hover:border-dcc hover:text-dcc">
                  Call
                </a>
                <a href={`https://wa.me/${SITE.contact.whatsapp.replace(/\s/g, "").replace("+", "")}`} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-whatsapp-btn"
                  className="flex min-h-[48px] items-center justify-center border border-zinc-700 font-mono text-xs uppercase tracking-[0.18em] text-zinc-200 transition-colors hover:border-dcc hover:text-dcc">
                  WhatsApp
                </a>
              </div>
              <Link to="/careers" data-testid="mobile-nav-careers-link"
                className="mt-6 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:text-dcc">
                Careers at DCC
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
